import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ContactFormState, ContactRecordType, CustomField } from '@/lib/contacts/types'
import { CustomFieldFormControl } from '@/pages/CustomFieldFormControl'
import { Textarea } from '@/components/ui/textarea'
import { COUNTRY_CODES, countryName } from '@/lib/countries'

export type SetContactForm = React.Dispatch<React.SetStateAction<ContactFormState>>

export function PostalAddressFields({
  form,
  setForm,
}: {
  form: ContactFormState
  setForm: SetContactForm
}) {
  const update = (field: keyof ContactFormState) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [field]: event.target.value }))
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h2 className="text-subheading">Postal address</h2>
      {(
        [
          ['Address line 1', 'addressLine1'],
          ['Address line 2', 'addressLine2'],
          ['City', 'city'],
          ['Region', 'region'],
          ['Postcode', 'postalCode'],
        ] as const
      ).map(([label, field]) => (
        <FormField key={field} label={label} htmlFor={`contact-${field}`}>
          <Input id={`contact-${field}`} value={form[field] as string} onChange={update(field)} />
        </FormField>
      ))}
      <FormField label="Country" htmlFor="contact-country">
        <Select
          value={form.countryCode}
          onValueChange={(value) => setForm((current) => ({ ...current, countryCode: value }))}
        >
          <SelectTrigger id="contact-country">
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((code) => (
              <SelectItem key={code} value={code}>
                {countryName(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    </div>
  )
}

export function ContactRecordTypeField({
  currentRecordType,
  editing,
  setForm,
}: {
  currentRecordType: ContactRecordType
  editing: boolean
  setForm: SetContactForm
}) {
  return (
    <FormField label="Record type" htmlFor="contact-record-type">
      <Select
        value={currentRecordType}
        disabled={editing}
        onValueChange={(recordType) =>
          setForm((current) => ({
            ...current,
            recordType: recordType as ContactRecordType,
            customFieldValues: {},
          }))
        }
      >
        <SelectTrigger id="contact-record-type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="person">Person</SelectItem>
          <SelectItem value="organization">Organization</SelectItem>
        </SelectContent>
      </Select>
    </FormField>
  )
}

export function NameField({
  currentRecordType,
  name,
  nameError,
  setForm,
}: {
  currentRecordType: ContactRecordType
  name: string
  nameError?: string
  setForm: SetContactForm
}) {
  return (
    <FormField label="Name" htmlFor="contact-name" required error={nameError}>
      <Input
        id="contact-name"
        value={name}
        autoComplete={currentRecordType === 'person' ? 'name' : 'organization'}
        onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
      />
    </FormField>
  )
}

export function PersonContactFields({
  email,
  emailError,
  phone,
  setForm,
}: {
  email: string
  emailError?: string
  phone: string
  setForm: SetContactForm
}) {
  return (
    <>
      <FormField label="Email" htmlFor="person-email" error={emailError}>
        <Input
          id="person-email"
          type="email"
          placeholder="jane@acme.com"
          value={email}
          autoComplete="email"
          onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
        />
      </FormField>
      <FormField label="Phone" htmlFor="person-phone">
        <Input
          id="person-phone"
          type="tel"
          placeholder="+1 415 555 0142"
          value={phone}
          autoComplete="tel"
          onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
        />
      </FormField>
    </>
  )
}

export function TaxExemptFields({
  taxExempt,
  taxExemptNote,
  taxExemptReason,
  setForm,
}: {
  taxExempt: boolean
  taxExemptNote: string
  taxExemptReason: string
  setForm: SetContactForm
}) {
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="contact-tax-exempt"
          checked={taxExempt}
          onCheckedChange={(checked) =>
            setForm((current) => ({
              ...current,
              taxExempt: checked === true,
              taxExemptNote: checked === true ? current.taxExemptNote : '',
            }))
          }
        />
        <Label htmlFor="contact-tax-exempt">Tax-Exempt</Label>
      </div>
      {taxExempt && (
        <>
          <FormField label="Exempt reason" htmlFor="contact-tax-exempt-reason" required>
            <Select
              value={taxExemptReason}
              onValueChange={(value) =>
                setForm((current) => ({ ...current, taxExemptReason: value }))
              }
            >
              <SelectTrigger id="contact-tax-exempt-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="exempt">Exempt</SelectItem>
                <SelectItem value="reverse_charge">Reverse charge</SelectItem>
                <SelectItem value="outside_scope">Outside scope</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Certificate note" htmlFor="contact-tax-exempt-note" required>
            <Textarea
              id="contact-tax-exempt-note"
              value={taxExemptNote}
              placeholder="Certificate reference and expiry"
              onChange={(event) =>
                setForm((current) => ({ ...current, taxExemptNote: event.target.value }))
              }
            />
          </FormField>
        </>
      )}
    </div>
  )
}

export function CustomFieldControls({
  customFields,
  customFieldValues,
  setForm,
}: {
  customFields: CustomField[]
  customFieldValues: ContactFormState['customFieldValues']
  setForm: SetContactForm
}) {
  return customFields.map((customField) => (
    <CustomFieldFormControl
      key={customField.id}
      customField={customField}
      value={customFieldValues[String(customField.id)]}
      onChange={(value) =>
        setForm((current) => ({
          ...current,
          customFieldValues: {
            ...current.customFieldValues,
            [String(customField.id)]: value,
          },
        }))
      }
    />
  ))
}
